import type { Router, RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * New in the migration — there was no routing service.
 *
 * Services currently reach the router by being handed `this.$router` from a component, or by
 * dispatching a Vuex action that takes a `router` argument (see AUTH_REQUIRED). Once services
 * are plain factory functions with no component to borrow from, they need somewhere to get it:
 * AuthorBase solves this with a `GlobalRouter` singleton set at bootstrap, and this is the
 * same thing.
 *
 * Deliberately thin. It is not a place to put navigation policy — just enough to let code
 * outside a component navigate, and to make "is there even a router yet?" answerable, since on
 * the server and in specs there often isn't.
 */

let GlobalRouter: Router | undefined

const useRouter = () => {
  /**
   * Client only, deliberately.
   *
   * Under SSR the router is created per request, so holding one in a module-level singleton
   * would hand one visitor's router to another's request — the same cross-request leak that
   * `base/store/defineState` exists to prevent for state. Server-side navigation in Quasar goes
   * through preFetch's `redirect()` and the route guards, not through `router.push`, so there is
   * nothing on the server that needs this. `push` degrades to a no-op there.
   *
   * If a genuine server-side need appears, the router belongs in the request context alongside
   * the stores, not here.
   */
  const init = (router: Router) => {
    if (process.env.SERVER) {
      return
    }

    GlobalRouter = router
  }

  const router = (): Router | undefined => GlobalRouter

  // vue-router 4 exposes `currentRoute` as a Ref, so it is read through `.value`.
  const currentRoute = (): RouteLocationNormalizedLoaded | undefined => GlobalRouter?.currentRoute?.value

  /**
   * Navigation from outside a component. RouteLocationNormalizedLoaded-guard redirects reject the navigation promise,
   * which is expected rather than exceptional, so it is swallowed here the same way the call
   * sites already do (`.catch(() => {})` appears throughout the mixins).
   */
  const push = (location: string | object): Promise<void> => {
    if (!GlobalRouter) {
      return Promise.resolve()
    }

    return GlobalRouter.push(location as any).then(() => void 0).catch(() => void 0)
  }

  const pushToName = (name: string, params?: object): Promise<void> => {
    return push({ name, params })
  }

  const reset = () => {
    GlobalRouter = void 0
  }

  return {
    init,
    router,
    currentRoute,
    push,
    pushToName,
    reset
  }
}

export { useRouter, GlobalRouter }
